/**
 * @fileoverview State, effects, and field handlers for the company certifications section.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { logger } from "../../../utils/logger.js";
import {
  getAcceptedPaymentMethodsOptions,
  getAnnualSalesVolumeOptions,
  getAvailableSectorsOptions,
  getAverageDeliveryTimesOptions,
  getCertificationsOptions,
  getCivilLiabilityInsuranceOptions,
  getIMMEXPermitOptions,
  getPracticesImplementedOptions,
  getREPSEProviderOptions,
  getYearsOfExperienceOptions,
} from "../../../data/selectOptions";
import { getCompanyFieldValue } from "../../../utils/common";
import { useFieldValidation } from "../../../hooks/useFieldValidation";

function useCertificationsPolicyState({ company, selectedProfile, t }) {
  const REPSEProviderOptions = getREPSEProviderOptions(t);
  const averageDeliveryTimesOptions = getAverageDeliveryTimesOptions(t);
  const annualSalesVolumeOptions = getAnnualSalesVolumeOptions(t);
  const acceptedPaymentMethodsOptions = getAcceptedPaymentMethodsOptions(t);
  const civilLiabilityInsuranceOptions = getCivilLiabilityInsuranceOptions(t);
  const IMMEXPermitOptions = getIMMEXPermitOptions(t);
  const practicesImplementedOptions = getPracticesImplementedOptions(t);

  const [esProveedorREPSE, setEsProveedorREPSE] = useState(
    getCompanyFieldValue(company, "esProveedorREPSE", selectedProfile, ""),
  );
  const [tiemposPromedioEntrega, setTiemposPromedioEntrega] = useState(
    getCompanyFieldValue(
      company,
      "tiemposPromedioDeEntrega",
      selectedProfile,
      "",
    ),
  );
  const [volumenDeVentasAnuales, setVolumenDeVentasAnuales] = useState(
    getCompanyFieldValue(
      company,
      "volumenDeVentasAnuales",
      selectedProfile,
      "",
    ),
  );
  const [metodosDePagoAceptados, setMetodosDePagoAceptados] = useState(
    getCompanyFieldValue(
      company,
      "metodosDePagoAceptados",
      selectedProfile,
      "",
    ),
  );
  const [seguroDeResponsabilidadCivil, setSeguroDeResponsabilidadCivil] =
    useState(
      getCompanyFieldValue(
        company,
        "seguroDeResponsabilidadCivil",
        selectedProfile,
        "",
      ),
    );
  const [permisoIMMEX, setPermisoIMMEX] = useState(
    getCompanyFieldValue(company, "permisoIMMEX", selectedProfile, ""),
  );
  const [practicasImplementadas, setPracticasImplementadas] = useState(
    getCompanyFieldValue(
      company,
      "practicasImplementadas",
      selectedProfile,
      "",
    ),
  );

  return {
    REPSEProviderOptions,
    averageDeliveryTimesOptions,
    annualSalesVolumeOptions,
    acceptedPaymentMethodsOptions,
    civilLiabilityInsuranceOptions,
    IMMEXPermitOptions,
    practicesImplementedOptions,
    esProveedorREPSE,
    setEsProveedorREPSE,
    tiemposPromedioEntrega,
    setTiemposPromedioEntrega,
    volumenDeVentasAnuales,
    setVolumenDeVentasAnuales,
    metodosDePagoAceptados,
    setMetodosDePagoAceptados,
    seguroDeResponsabilidadCivil,
    setSeguroDeResponsabilidadCivil,
    permisoIMMEX,
    setPermisoIMMEX,
    practicasImplementadas,
    setPracticasImplementadas,
  };
}

function useSyncCertificationsFromCompany({
  company,
  selectedProfile,
  setSelectedCertifications,
  setIsMemberOfBusinessAssociation,
  setSectors,
}) {
  useEffect(() => {
    const certs = getCompanyFieldValue(
      company,
      "certificaciones",
      selectedProfile,
      [],
    );
    if (certs && certs.length > 0) {
      setSelectedCertifications(certs);
    }
    if (company && company.perteneceAUnaAsociacionEmpresarial !== undefined) {
      setIsMemberOfBusinessAssociation(
        company.perteneceAUnaAsociacionEmpresarial === true,
      );
    }
    const sects = getCompanyFieldValue(
      company,
      "sectores",
      selectedProfile,
      [],
    );
    if (sects && sects.length > 0) {
      const formattedSectors = sects.map((sector) => ({
        value: sector.value,
        experience: sector.experience || null,
      }));
      setSectors(formattedSectors);
    }
  }, [
    company,
    selectedProfile,
    setSelectedCertifications,
    setIsMemberOfBusinessAssociation,
    setSectors,
  ]);
}

function useValidationChangeNotifier({ onValidationChange, hasErrors }) {
  const prevHasErrorsRef = useRef(false);
  const onValidationChangeRef = useRef(onValidationChange);

  // Update the ref when onValidationChange changes
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    if (
      onValidationChangeRef.current &&
      prevHasErrorsRef.current !== hasErrors
    ) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChangeRef.current(hasErrors);
    }
  }, [hasErrors]);
}

function applyCertificationToggle({
  value,
  selectedCertifications,
  setSelectedCertifications,
  onFieldChange,
}) {
  let newSelectedCertifications;

  logger.info("selectedCertifications", { data: selectedCertifications });

  if (selectedCertifications.includes(value)) {
    newSelectedCertifications = selectedCertifications.filter(
      (certification) => certification !== value,
    );
  } else {
    newSelectedCertifications = [...selectedCertifications, value];
  }

  setSelectedCertifications(newSelectedCertifications);

  if (onFieldChange) {
    onFieldChange("certificaciones", newSelectedCertifications);
  }
}

function applySectorToggle({
  sectorValue,
  sectors,
  setSectors,
  onFieldChange,
}) {
  const sectorExists = sectors.some((sector) => sector.value === sectorValue);

  if (sectorExists) {
    const newSectors = sectors.filter(
      (sector) => sector.value !== sectorValue,
    );
    setSectors(newSectors);

    if (onFieldChange) {
      onFieldChange("sectores", newSectors);
    }
  } else {
    const newSectors = [...sectors, { value: sectorValue, experience: null }];
    setSectors(newSectors);

    if (onFieldChange) {
      onFieldChange("sectores", newSectors);
    }
  }
}

function applySectorExperienceChange({
  sectorValue,
  selectedOption,
  sectors,
  setSectors,
  onFieldChange,
}) {
  const newSectors = sectors.map((sector) => {
    if (sector.value === sectorValue) {
      return { ...sector, experience: selectedOption.value };
    }
    return sector;
  });

  setSectors(newSectors);

  if (onFieldChange) {
    onFieldChange("sectores", newSectors);
  }
}

function applyRadioChange({
  field,
  value,
  setIsMemberOfBusinessAssociation,
  onFieldChange,
}) {
  if (field === "perteneceAUnaAsociacionEmpresarial") {
    setIsMemberOfBusinessAssociation(value);
  }

  if (onFieldChange) {
    onFieldChange(field, value);
  }
}

/**
 * Certifications section state and handlers.
 * @param {Object} params
 * @param {Object} params.company
 * @param {Function} params.onFieldChange
 * @param {Function} params.onValidationChange
 * @param {number} [params.selectedProfile]
 */
export default function useCertificationsSection({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) {
  const { t } = useTranslation();
  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    capacidadInstalada: "",
    clientesPrincipalesYReferenciasComerciales: "",
  });

  const [selectedCertifications, setSelectedCertifications] = useState(
    getCompanyFieldValue(company, "certificaciones", selectedProfile, []),
  );
  const [isMemberOfBusinessAssociation, setIsMemberOfBusinessAssociation] =
    useState(company?.perteneceAUnaAsociacionEmpresarial === true);
  const [sectors, setSectors] = useState(
    getCompanyFieldValue(company, "sectores", selectedProfile, []),
  );
  const [documentedSystem, setDocumentedSystem] = useState(
    getCompanyFieldValue(
      company,
      "tieneSistemaDocumentado",
      selectedProfile,
      null,
    ),
  );

  const certifications = getCertificationsOptions(t);
  const availableSectors = getAvailableSectorsOptions(t);
  const yearsOptions = getYearsOfExperienceOptions(t);
  const policy = useCertificationsPolicyState({ company, selectedProfile, t });

  useSyncCertificationsFromCompany({
    company,
    selectedProfile,
    setSelectedCertifications,
    setIsMemberOfBusinessAssociation,
    setSectors,
  });
  useValidationChangeNotifier({ onValidationChange, hasErrors });

  const handleCheckboxChange = (value) =>
    applyCertificationToggle({
      value,
      selectedCertifications,
      setSelectedCertifications,
      onFieldChange,
    });

  const handleSectorCheckboxChange = (sectorValue) =>
    applySectorToggle({
      sectorValue,
      sectors,
      setSectors,
      onFieldChange,
    });

  const handleSectorExperienceChange = (sectorValue, selectedOption) =>
    applySectorExperienceChange({
      sectorValue,
      selectedOption,
      sectors,
      setSectors,
      onFieldChange,
    });

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const handleRadioChange = (field, value) =>
    applyRadioChange({
      field,
      value,
      setIsMemberOfBusinessAssociation,
      onFieldChange,
    });

  return {
    t,
    selectedCertifications,
    sectors,
    isMemberOfBusinessAssociation,
    documentedSystem,
    setDocumentedSystem,
    certifications,
    availableSectors,
    yearsOptions,
    handleCheckboxChange,
    handleSectorCheckboxChange,
    handleSectorExperienceChange,
    handleInputChange,
    handleRadioChange,
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    ...policy,
  };
}
